import type { BrandingConfig } from "@platform/core";
import { Button, Field, Input, Skeleton } from "@platform/ui";
import { useConsole } from "../../context";
import { useT } from "../../i18n";
import { BrandPreviewCard } from "../../lib/brand-preview";
import { cleanHeroText, heroPlaceholder, HeroTextFields } from "../../lib/hero-text";
import { SettingErrors, useSetting } from "../../lib/use-setting";
import { ErrorBanner, PageCard, Section } from "../../layout/Page";

export function BrandingPage() {
  const { t } = useT();
  const { can } = useConsole();
  const s = useSetting("branding");
  const b = s.draft;
  const editable = can("settings:write");
  if (s.query.isError)
    return (
      <PageCard>
        <ErrorBanner error={s.query.error} onRetry={() => void s.query.refetch()} />
      </PageCard>
    );
  if (!b)
    return (
      <PageCard>
        <Skeleton className="m-6 h-48" />
      </PageCard>
    );
  const set = (patch: Partial<BrandingConfig>) => s.setDraft({ ...b, ...patch });
  const clean = (): BrandingConfig => {
    const { logoUrl, supportEmail, heroTitle, heroBody, ...rest } = b;
    return {
      ...rest,
      ...(logoUrl ? { logoUrl } : {}),
      ...(supportEmail ? { supportEmail } : {}),
      ...cleanHeroText({ ...(heroTitle ? { heroTitle } : {}), ...(heroBody ? { heroBody } : {}) }),
    };
  };
  const validColor = /^#[0-9a-fA-F]{6}$/.test(b.primaryColor);
  return (
    <PageCard>
      <SettingErrors state={s} conflictLabel={t("signin.conflict")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          s.save.mutate(clean());
        }}
      >
        <fieldset disabled={!editable}>
          <Section title={t("nav.branding")}>
            <div className="grid gap-x-4 sm:grid-cols-2">
              <Field label={t("branding.productName")} required>
                {(p) => (
                  <Input
                    {...p}
                    value={b.productName}
                    maxLength={60}
                    onChange={(e) => set({ productName: e.target.value })}
                  />
                )}
              </Field>
              <Field label={t("branding.logoUrl")}>
                {(p) => (
                  <Input
                    {...p}
                    type="url"
                    value={b.logoUrl ?? ""}
                    onChange={(e) => set({ logoUrl: e.target.value })}
                  />
                )}
              </Field>
              <Field label={t("branding.primaryColor")} error={validColor ? undefined : "#rrggbb"}>
                {(p) => (
                  <div className="flex gap-2">
                    <input
                      type="color"
                      aria-label={t("branding.primaryColor")}
                      value={validColor ? b.primaryColor : "#009be5"}
                      onChange={(e) => set({ primaryColor: e.target.value })}
                      className="h-10 w-12 cursor-pointer rounded border border-line"
                    />
                    <Input
                      {...p}
                      value={b.primaryColor}
                      onChange={(e) => set({ primaryColor: e.target.value })}
                    />
                  </div>
                )}
              </Field>
              <Field label={t("branding.supportEmail")}>
                {(p) => (
                  <Input
                    {...p}
                    type="email"
                    value={b.supportEmail ?? ""}
                    onChange={(e) => set({ supportEmail: e.target.value })}
                  />
                )}
              </Field>
            </div>
          </Section>
          <Section title={t("heroText.section")}>
            <p className="mb-4 text-sm text-ink-2">{t("heroText.globalHint")}</p>
            <HeroTextFields
              value={b}
              onChange={(v) => set({ heroTitle: v.heroTitle, heroBody: v.heroBody })}
              placeholder={heroPlaceholder(b.productName)}
            />
          </Section>
        </fieldset>
        <Section title={t("branding.preview")}>
          <BrandPreviewCard
            name={b.productName}
            color={validColor ? b.primaryColor : "#009be5"}
            {...(b.logoUrl ? { logoUrl: b.logoUrl } : {})}
          />
        </Section>
        {editable && (
          <div className="flex justify-end px-6 pb-5">
            <Button
              type="submit"
              data-testid="save-settings-button"
              loading={s.save.isPending}
              disabled={!validColor || !b.productName.trim()}
            >
              {t("common.save")}
            </Button>
          </div>
        )}
      </form>
    </PageCard>
  );
}
